import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { Field, Select, TextArea, SubmitButton, ThankYou } from "@/components/Field";

const RE_IMG = "https://images.unsplash.com/photo-1682184805271-11671b7ecf4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTEzNTIyOTV8MA&ixlib=rb-4.1.0&q=85";
const RE_IMG2 = "https://images.unsplash.com/photo-1518733057094-95b53143d2a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTEzNTIyOTV8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { title: "Buying", text: "A patient, honest search for the home that fits your life — from first tour to final keys." },
  { title: "Selling", text: "Considered positioning, beautiful presentation and calm negotiation for your property." },
  { title: "Investment / Consultation", text: "Clear-eyed guidance on Los Angeles neighborhoods, value and long-term potential." },
];

export default function RealEstate() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", area: "", budget: "", timeline: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/realestate-leads", form);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="realestate-page" className="pt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">Real Estate — Los Angeles</p>
              <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight leading-[1.05] mb-6">
                Home, approached like <span className="italic">art.</span>
              </h1>
              <p className="text-sm text-[#4A4A4A] leading-relaxed max-w-md">
                As a realtor, I bring a photographer's eye to every property — helping you buy, sell or invest across
                Los Angeles with honesty, taste and quiet confidence.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <ImageReveal src={RE_IMG} alt="Los Angeles interior" className="aspect-[4/3]" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-24">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1} className="border border-[#1A1A1A]/10 p-8 bg-white/40">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">0{i + 1}</p>
              <h3 className="font-serif text-2xl font-light mb-3">{s.title}</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">{s.text}</p>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-12 mt-24 pb-28">
          <div className="lg:col-span-5">
            <ImageReveal src={RE_IMG2} alt="Interior detail" className="aspect-[3/4]" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <ThankYou testId="realestate-success" text="Your inquiry has been received. I'll reach out shortly to talk through your goals." />
            ) : (
              <Reveal>
                <h2 className="font-serif text-4xl font-light tracking-tight mb-3">Start the <span className="italic">conversation.</span></h2>
                <p className="text-sm text-[#4A4A4A] mb-10">Tell me a little about what you're looking for.</p>
                <form onSubmit={submit} className="space-y-8" data-testid="realestate-form" id="re-inquiry">
                  <Field label="Name" id="re-name" required value={form.name} onChange={set("name")} />
                  <div className="grid sm:grid-cols-2 gap-8">
                    <Field label="Email" id="re-email" type="email" required autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} />
                    <Field label="Phone" id="re-phone" type="tel" required autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <Select label="I'm interested in" id="re-interest" optional options={["Buy", "Sell", "Invest", "Consultation"]} value={form.interest} onChange={set("interest")} />
                    <Field label="Preferred Area" id="re-area" placeholder="e.g. Silver Lake" optional value={form.area} onChange={set("area")} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <Select label="Budget Range" id="re-budget" optional
                      options={["Under $500k", "$500k – $1M", "$1M – $2M", "$2M – $5M", "$5M+", "Prefer to discuss"]}
                      value={form.budget} onChange={set("budget")} />
                    <Select label="Timeline" id="re-timeline" optional
                      options={["ASAP", "1–3 months", "3–6 months", "6+ months", "Just exploring"]}
                      value={form.timeline} onChange={set("timeline")} />
                  </div>
                  <TextArea label="Message" id="re-message" optional value={form.message} onChange={set("message")} />
                  <div className="pt-4">
                    <SubmitButton id="re-submit" loading={loading}>Work With Bahare</SubmitButton>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
